import React, { useContext, useMemo } from "react";
import dayjs from "dayjs";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { BookingContext } from "../../context/BookingContext";
import { format } from "../../utils";
import { Text, ThemeStore, Weekly } from "@foris/avocado-suite";
import WeekTooltipLabel from "@components/week-tooltip-label";
import { getIsCurrentWeek } from "@modules/booking/utils/classroomEvents";
import ClassroomsResumeTooltip from "../classrooms-resume-tooltip";
import { getDateNumber } from "@utils/formats";
import css from "./requestDays.module.scss";

const dayByValue = (value: string) => {
  const days = [
    "day-list.short-names.monday",
    "day-list.short-names.monday",
    "day-list.short-names.tuesday",
    "day-list.short-names.wednesday",
    "day-list.short-names.thursday",
    "day-list.short-names.friday",
    "day-list.short-names.saturday",
    "day-list.short-names.monday",
  ];

  return value ? days[value] : "";
};

const Days = () => {
  const { state } = useContext(BookingContext);
  const filterSelected = state?.search?.intervals?.filter(value => value.selected);
  const sorted = filterSelected?.sort((a, b) => {
    const dateA = new Date(a.startingDate)?.getTime();
    const dateB = new Date(b.startingDate)?.getTime();
    return dateA - dateB;
  });
  const themeMode = ThemeStore.useThemeStore(state => state.theme.mode);
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const weeklyItems = useMemo(() => {
    return state?.search?.intervals?.map(week => {
      const { isCurrentWeek, isBeforeCurrentWeek } = getIsCurrentWeek(week);
      const isDisabled = !isCurrentWeek && isBeforeCurrentWeek;

      return {
        id: week.id,
        label: week.value,
        isDisabled,
        isSelected: week?.selected,
        tooltipLabel: (
          <WeekTooltipLabel
            weekLabel={`S${week.value}`}
            startingDate={dayjs(week.startingDate)?.format("DD.MM.YYYY")}
            endingDate={dayjs(week.endingDate)?.format("DD.MM.YYYY")}
          />
        ),
        className: isCurrentWeek ? "currentWeek" : "",
        onClick: () => null,
      };
    });
  }, [state?.search?.intervals]);

  const dateTimes = useMemo(() => {
    return state?.search?.dateTime?.map(date => {
      const find = date?.recurrence
        ? format?.DAYS_OPTIONS().find(value => value.value === date?.day?.value)
        : null;
      const dayNumber: string = new Date(date?.day?.value).getUTCDay().toString();
      const day = date?.recurrence ? find?.label.substring(0, 3) : t(dayByValue(dayNumber));
      const start = date?.start?.value?.label;
      const end = date?.end?.value?.label;
      const title = `${day}, ${start} - ${end}`;

      return {
        dateIndex: date?.index,
        title,
      };
    });
  }, [state?.search?.dateTime, language]);

  const selectedDatesByClassroom = useMemo(() => {
    const selectedClassrooms = state?.search?.selectedClassrooms ?? [];
    return selectedClassrooms
      ?.map(({ classroom, dates }) => {
        return dates?.map(({ day, start, end }) => {
          const date = dayjs(day?.dateValue).format("DD.MM.YYYY");
          const [startHour, startMinute] = start?.value?.value?.split(":");
          const [endHour, endMinute] = end?.value?.value?.split(":");

          return {
            date: `${date}  ${getDateNumber(startHour || 0)}:${getDateNumber(
              startMinute || 0,
            )} - ${getDateNumber(endHour || 0)}:${getDateNumber(endMinute || 0)}`,
            classroom: classroom?.name,
          };
        });
      })
      .flat();
  }, [state?.search?.selectedClassrooms]);

  return (
    <div className={cx(css.requestDays, themeMode === "dark" && css.requestDays__dark)}>
      <div className={css.requestDays_item}>
        <Text className={css.itemLabel} type="sm">
          {t("booking.booking-request.resume.recurrence")}
        </Text>

        <Text className={css.itemValue}>
          {state?.search?.recurrence
            ? t("booking.booking-form.type-selector.options.recurring")
            : t("booking.booking-form.type-selector.options.single")}
        </Text>
      </div>

      <div className={css.requestDays_itemGroup}>
        {dateTimes?.map(dateTime => (
          <div className={css.requestDays_item} key={dateTime?.dateIndex}>
            <Text className={css.itemLabel} type="sm">
              {`${t("booking.booking-request.resume.day")} ${dateTime?.dateIndex}`}
            </Text>

            <Text className={css.itemValue}>{dateTime?.title}</Text>
          </div>
        ))}

        <ClassroomsResumeTooltip classroomsDates={selectedDatesByClassroom} />
      </div>

      {sorted?.length > 1 && (
        <div className={css.requestDays_itemGroup}>
          <div className={css.requestDays_item}>
            <Text className={css.itemLabel} type="sm">
              {t("booking.booking-form.date-selector.week-row.start-week")}
            </Text>

            <Text className={css.itemValue}>
              <span className={css.itemValue_week}>
                {t("booking.booking-form.date-selector.week-row.week", {
                  week: sorted[0]?.value,
                })}
              </span>{" "}
              {dayjs(sorted[0]?.startingDate).format("DD.MM.YYYY")}
              {" - "}
              {dayjs(sorted[0]?.endingDate).format("DD.MM.YYYY")}
            </Text>
          </div>

          <div className={css.requestDays_item}>
            <Text className={css.itemLabel} type="sm">
              {t("booking.booking-form.date-selector.week-row.end-week")}
            </Text>

            <Text className={css.itemValue}>
              <span className={css.itemValue_week}>
                {t("booking.booking-form.date-selector.week-row.week", {
                  week: sorted[sorted.length - 1]?.value,
                })}
              </span>{" "}
              {dayjs(sorted[sorted?.length - 1]?.startingDate).format("DD.MM.YYYY")}
              {" - "}
              {dayjs(sorted[sorted?.length - 1]?.endingDate).format("DD.MM.YYYY")}
            </Text>
          </div>
        </div>
      )}

      {state?.search?.recurrence && <Weekly className="editionWeekly" weeklyItems={weeklyItems} />}
    </div>
  );
};

export default Days;
